import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ArrowLeft, Building2, Mail, Phone, Briefcase, FileText,
  ChevronDown, ChevronUp, CheckCircle, Clock, Loader2, Save,
  MessageSquare, Plus, Calendar, AlertTriangle, ArrowRight, Activity,
  DollarSign, Target, CircleDot
} from 'lucide-react';
import { casesAPI, partnersAPI } from '@/lib/api';
import { toast } from 'sonner';

const STATUSES = [
  { key: 'assigned', label: 'Assigned', color: 'bg-blue-100 text-blue-700 border-blue-200', step: 1 },
  { key: 'financials_in_preparation', label: 'Financials in Preparation', color: 'bg-amber-100 text-amber-700 border-amber-200', step: 2 },
  { key: 'under_review', label: 'Under Review', color: 'bg-purple-100 text-purple-700 border-purple-200', step: 3 },
  { key: 'sent_to_cs', label: 'Sent to CS for Filing', color: 'bg-sky-100 text-sky-700 border-sky-200', step: 4 },
  { key: 'filing_in_progress', label: 'Filing in Progress', color: 'bg-orange-100 text-orange-700 border-orange-200', step: 5 },
  { key: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700 border-green-200', step: 6 },
];

const TASK_STATUS_COLORS = {
  not_started: 'bg-slate-100 text-slate-500',
  in_progress: 'bg-amber-100 text-amber-700',
  completed: 'bg-green-100 text-green-700',
};

const DOC_STATUS_COLORS = {
  pending: 'bg-slate-100 text-slate-500',
  uploaded: 'bg-blue-100 text-blue-700',
  approved: 'bg-green-100 text-green-700',
};

const PHASE_COLORS = {
  'Financial Preparation': 'border-l-blue-500 bg-blue-50/30',
  'Statutory Audit': 'border-l-violet-500 bg-violet-50/30',
  'Tax Clearances': 'border-l-amber-500 bg-amber-50/30',
  'Financial Closure': 'border-l-red-500 bg-red-50/30',
  'BS Closure Actions': 'border-l-teal-500 bg-teal-50/30',
  'Annual Filings (CCFS Window)': 'border-l-cyan-500 bg-cyan-50/30',
  'Pending Compliances': 'border-l-orange-500 bg-orange-50/30',
  'Strike-off Preparation (Sec 248)': 'border-l-rose-500 bg-rose-50/30',
  'STK-2 Filing': 'border-l-indigo-500 bg-indigo-50/30',
  'Post-Filing Monitoring': 'border-l-emerald-500 bg-emerald-50/30',
  'General': 'border-l-slate-400 bg-slate-50/30',
};

function groupTasksByPhase(tasks) {
  const groups = [];
  const seen = new Set();
  let globalIdx = 0;
  for (const task of tasks) {
    const phase = task.phase || 'General';
    if (!seen.has(phase)) {
      seen.add(phase);
      groups.push({ phase, tasks: [] });
    }
    groups.find(g => g.phase === phase).tasks.push({ ...task, globalIdx });
    globalIdx++;
  }
  return groups;
}

function fmt(n) {
  if (!n && n !== 0) return '—';
  return '\u20B9' + Math.round(n).toLocaleString('en-IN');
}

export default function AdminCaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [newKind, setNewKind] = useState('note');
  const [showCAScope, setShowCAScope] = useState(true);
  const [showCSScope, setShowCSScope] = useState(true);
  const [showActivity, setShowActivity] = useState(false);
  const [collapsedPhases, setCollapsedPhases] = useState({});

  useEffect(() => {
    Promise.all([
      casesAPI.get(id),
      partnersAPI.getAll(),
    ]).then(([caseRes, partnersRes]) => {
      setCaseData(caseRes.data);
      setPartners(partnersRes.data);
    }).catch(() => toast.error('Failed to load case')).finally(() => setLoading(false));
  }, [id]);

  const save = useCallback(async (updates) => {
    setSaving(true);
    try {
      const res = await casesAPI.update(id, updates);
      setCaseData(res.data);
      toast.success('Saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  }, [id]);

  const updateStatus = (newStatus) => save({ status: newStatus });
  const assignPartner = (field, partnerId) => save({ [field]: partnerId });

  const updateCATask = (taskIdx, field, value) => {
    const updated = { ...caseData.ca_scope };
    updated.tasks = [...updated.tasks];
    updated.tasks[taskIdx] = { ...updated.tasks[taskIdx], [field]: value };
    save({ ca_scope: updated });
  };

  const updateCSTask = (taskIdx, field, value) => {
    const updated = { ...caseData.cs_scope };
    updated.tasks = [...updated.tasks];
    updated.tasks[taskIdx] = { ...updated.tasks[taskIdx], [field]: value };
    save({ cs_scope: updated });
  };

  const updateCADoc = (docIdx, field, value) => {
    const updated = { ...caseData.ca_scope };
    updated.documents = [...updated.documents];
    updated.documents[docIdx] = { ...updated.documents[docIdx], [field]: value };
    save({ ca_scope: updated });
  };

  const updateCSDoc = (docIdx, field, value) => {
    const updated = { ...caseData.cs_scope };
    updated.documents = [...updated.documents];
    updated.documents[docIdx] = { ...updated.documents[docIdx], [field]: value };
    save({ cs_scope: updated });
  };

  const addPendingFY = () => {
    const updated = { ...caseData.ca_scope };
    const nextFY = `FY ${new Date().getFullYear() - 1}-${String(new Date().getFullYear()).slice(2)}`;
    updated.pending_fys = [...(updated.pending_fys || []), nextFY];
    save({ ca_scope: updated });
  };

  const removePendingFY = (idx) => {
    const updated = { ...caseData.ca_scope };
    updated.pending_fys = updated.pending_fys.filter((_, i) => i !== idx);
    save({ ca_scope: updated });
  };

  const updatePendingFY = (idx, value) => {
    const updated = { ...caseData.ca_scope };
    updated.pending_fys = [...updated.pending_fys];
    updated.pending_fys[idx] = value;
    save({ ca_scope: updated });
  };

  const addNote = async () => {
    if (!newNote.trim()) return;
    try {
      await casesAPI.addNote(id, newNote.trim());
      setNewNote('');
      const res = await casesAPI.get(id);
      setCaseData(res.data);
      toast.success('Note added');
    } catch { toast.error('Failed to add note'); }
  };

  const addStream = async () => {
    if (!newNote.trim()) return;
    try {
      const token = localStorage.getItem('ratnatraya_token');
      const apiBase = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${apiBase}/api/admin/cases/${id}/status`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ text: newNote.trim(), kind: newKind }),
      });
      if (!res.ok) throw new Error('add failed');
      setNewNote('');
      setNewKind('note');
      const r = await casesAPI.get(id);
      setCaseData(r.data);
      toast.success('Status updated');
    } catch { toast.error('Failed to add status update'); }
  };

  const deleteCase = async () => {
    if (!window.confirm(`Delete case "${caseData.company_name}"? The source lead will be reverted from WON.`)) return;
    try {
      await casesAPI.delete(id);
      toast.success('Case deleted — lead reverted');
      navigate('/admin/cases');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete case');
    }
  };

  if (loading) return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!caseData) return <div className="p-8 text-center text-slate-500">Case not found</div>;

  const currentStatusIdx = STATUSES.findIndex(s => s.key === caseData.status);
  const caPartners = partners.filter(p => p.profession === 'CA');
  const csPartners = partners.filter(p => p.profession === 'CS');

  return (
    <div className="p-6 lg:p-8 max-w-5xl mx-auto" data-testid="admin-case-detail">
      {/* Back + Header */}
      <button onClick={() => navigate('/admin/cases')} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700 mb-4 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Cases
      </button>

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" data-testid="case-company-name">{caseData.company_name}</h1>
          <p className="text-slate-500 text-sm">{caseData.client_name} {caseData.client_email && `| ${caseData.client_email}`}</p>
        </div>
        <div className="flex items-center gap-3">
          {saving && <Loader2 className="w-5 h-5 animate-spin text-slate-400" />}
          {caseData.status !== 'filing_in_progress' && caseData.status !== 'completed' && (
            <Button
              size="sm"
              variant="ghost"
              className="h-8 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
              onClick={deleteCase}
              data-testid="delete-case-btn"
            >
              <AlertTriangle className="w-3 h-3 mr-1" /> Delete Case
            </Button>
          )}
        </div>
      </div>

      {/* Status Progress */}
      <Card className="mb-6" data-testid="status-progress">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-slate-700">Case Status</h3>
          </div>
          <div className="flex items-center gap-1">
            {STATUSES.map((s, i) => {
              const isActive = i === currentStatusIdx;
              const isDone = i < currentStatusIdx;
              return (
                <div key={s.key} className="flex items-center flex-1">
                  <button
                    onClick={() => updateStatus(s.key)}
                    className={`flex-1 py-2 px-1 text-[10px] font-medium text-center rounded-md border transition-all ${isActive ? s.color + ' ring-2 ring-offset-1 ring-slate-300' : isDone ? 'bg-green-50 text-green-600 border-green-200' : 'bg-slate-50 text-slate-400 border-slate-200 hover:border-slate-300'}`}
                    data-testid={`status-btn-${s.key}`}
                  >
                    {isDone && <CheckCircle className="w-3 h-3 inline mr-0.5 -mt-0.5" />}
                    {s.label}
                  </button>
                  {i < STATUSES.length - 1 && <ArrowRight className="w-3 h-3 text-slate-300 mx-0.5 shrink-0" />}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Partner Assignments */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card data-testid="ca-assignment">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <Briefcase className="w-4 h-4 text-[#1e3a5f]" />
              <h3 className="text-sm font-semibold text-slate-700">CA Assignment</h3>
            </div>
            <select
              value={caseData.ca_partner_id || ''}
              onChange={e => assignPartner('ca_partner_id', e.target.value || null)}
              className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:border-[#1e3a5f] focus:ring-1 focus:ring-[#1e3a5f] outline-none"
              data-testid="ca-partner-select"
            >
              <option value="">-- Unassigned --</option>
              {caPartners.map(p => <option key={p.id} value={p.id}>{p.name} ({p.firm_name})</option>)}
            </select>
          </CardContent>
        </Card>
        <Card data-testid="cs-assignment">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <FileText className="w-4 h-4 text-[#1e3a5f]" />
              <h3 className="text-sm font-semibold text-slate-700">CS Assignment</h3>
            </div>
            <select
              value={caseData.cs_partner_id || ''}
              onChange={e => assignPartner('cs_partner_id', e.target.value || null)}
              className="w-full h-10 rounded-md border border-slate-200 bg-white px-3 text-sm text-slate-700 focus:border-[#1e3a5f] focus:ring-1 focus:ring-[#1e3a5f] outline-none"
              data-testid="cs-partner-select"
            >
              <option value="">-- Unassigned --</option>
              {csPartners.map(p => <option key={p.id} value={p.id}>{p.name} ({p.firm_name})</option>)}
            </select>
          </CardContent>
        </Card>
      </div>

      {/* PROPOSAL SUMMARY */}
      {caseData.proposal_summary && (
        <Card className="mb-4 border-l-4 border-l-emerald-500" data-testid="proposal-summary-card">
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="w-4 h-4 text-emerald-600" />
              <h3 className="text-sm font-semibold text-slate-700">Agreed Proposal (v{caseData.proposal_summary.version})</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <p className="text-[10px] text-slate-400 uppercase font-semibold">Total Fee</p>
                <p className="text-lg font-bold text-emerald-700" data-testid="proposal-total-fee">{fmt(caseData.proposal_summary.total_fee)}</p>
              </div>
              <div>
                <p className="text-[10px] text-slate-400 uppercase font-semibold">Timeline</p>
                <p className="text-sm font-medium text-slate-800">{caseData.proposal_summary.timeline_weeks ? `${caseData.proposal_summary.timeline_weeks} weeks` : '—'}</p>
              </div>
              <div className="md:col-span-2">
                <p className="text-[10px] text-slate-400 uppercase font-semibold">Scope Summary</p>
                <p className="text-sm text-slate-600">{caseData.proposal_summary.scope_summary || '—'}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* CA SCOPE */}
      <Card className="mb-4">
        <button onClick={() => setShowCAScope(!showCAScope)} className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors">
          <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Briefcase className="w-4 h-4 text-[#1e3a5f]" /> CA Scope
          </span>
          {showCAScope ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>
        {showCAScope && (
          <div className="px-5 pb-5 space-y-5 border-t border-slate-100 pt-4">
            {/* Pending FYs */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold text-slate-600 uppercase">Pending Financial Years</p>
                <Button size="sm" variant="outline" className="h-7 text-xs gap-1" onClick={addPendingFY} data-testid="add-fy-btn"><Plus className="w-3 h-3" /> Add FY</Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {(caseData.ca_scope?.pending_fys || []).map((fy, i) => (
                  <div key={i} className="flex items-center gap-1 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
                    <input
                      className="bg-transparent text-sm text-amber-700 font-medium w-24 outline-none"
                      value={fy}
                      onChange={e => updatePendingFY(i, e.target.value)}
                    />
                    <button onClick={() => removePendingFY(i)} className="text-amber-400 hover:text-red-500 text-xs font-bold">x</button>
                  </div>
                ))}
                {(caseData.ca_scope?.pending_fys || []).length === 0 && <p className="text-xs text-slate-400">No pending FYs added yet</p>}
              </div>
            </div>

            {/* CA Tasks — Phase Grouped */}
            {(() => {
              const caTasks = caseData.ca_scope?.tasks || [];
              const caPhases = groupTasksByPhase(caTasks);
              const caCompleted = caTasks.filter(t => t.status === 'completed').length;
              return (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-600 uppercase">Tasks ({caCompleted}/{caTasks.length} done)</p>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5 mb-3">
                    <div className="bg-[#1e3a5f] h-1.5 rounded-full transition-all duration-500" style={{ width: `${caTasks.length > 0 ? (caCompleted / caTasks.length) * 100 : 0}%` }} />
                  </div>
                  <div className="space-y-3">
                    {caPhases.map(({ phase, tasks: phaseTasks }) => {
                      const phaseCompleted = phaseTasks.filter(t => t.status === 'completed').length;
                      const phaseColor = PHASE_COLORS[phase] || PHASE_COLORS['General'];
                      const isCollapsed = collapsedPhases[`ca_${phase}`];
                      return (
                        <div key={phase} className={`rounded-lg border-l-4 ${phaseColor}`} data-testid={`ca-phase-${phase.replace(/\s+/g, '-').toLowerCase()}`}>
                          <button onClick={() => setCollapsedPhases(prev => ({ ...prev, [`ca_${phase}`]: !prev[`ca_${phase}`] }))} className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-white/50 transition-colors">
                            <div className="flex items-center gap-2">
                              <CircleDot className="w-3.5 h-3.5 text-slate-500" />
                              <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">{phase}</span>
                              <span className="text-[10px] text-slate-400 font-medium">{phaseCompleted}/{phaseTasks.length}</span>
                            </div>
                            {isCollapsed ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronUp className="w-3.5 h-3.5 text-slate-400" />}
                          </button>
                          {!isCollapsed && (
                            <div className="px-4 pb-3 space-y-1.5">
                              {phaseTasks.map(task => (
                                <div key={task.globalIdx} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-3" data-testid={`ca-task-${task.globalIdx}`}>
                                  <p className={`text-sm flex-1 font-medium ${task.status === 'completed' ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{task.name}</p>
                                  <select value={task.status} onChange={e => updateCATask(task.globalIdx, 'status', e.target.value)} className={`text-xs font-medium px-2 py-1 rounded-full border-0 outline-none cursor-pointer ${TASK_STATUS_COLORS[task.status]}`}>
                                    <option value="not_started">Not Started</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="completed">Completed</option>
                                  </select>
                                  <input type="date" value={task.due_date || ''} onChange={e => updateCATask(task.globalIdx, 'due_date', e.target.value)} className="text-xs text-slate-500 border border-slate-200 rounded px-2 py-1" />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* CA Documents */}
            <div>
              <p className="text-xs font-semibold text-slate-600 uppercase mb-2">Documents</p>
              <div className="space-y-2">
                {(caseData.ca_scope?.documents || []).map((doc, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-3" data-testid={`ca-doc-${i}`}>
                    <p className="text-sm text-slate-700 flex-1 font-medium">{doc.name}</p>
                    <select
                      value={doc.status}
                      onChange={e => updateCADoc(i, 'status', e.target.value)}
                      className={`text-xs font-medium px-2 py-1 rounded-full border-0 outline-none cursor-pointer ${DOC_STATUS_COLORS[doc.status]}`}
                    >
                      <option value="pending">Pending</option>
                      <option value="uploaded">Uploaded</option>
                      <option value="approved">Approved</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* CS SCOPE */}
      <Card className="mb-4">
        <button onClick={() => setShowCSScope(!showCSScope)} className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors">
          <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <FileText className="w-4 h-4 text-[#1e3a5f]" /> CS Scope
          </span>
          {showCSScope ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>
        {showCSScope && (
          <div className="px-5 pb-5 space-y-5 border-t border-slate-100 pt-4">
            {/* CS Tasks — Phase Grouped */}
            {(() => {
              const csTasks = caseData.cs_scope?.tasks || [];
              const csPhases = groupTasksByPhase(csTasks);
              const csCompleted = csTasks.filter(t => t.status === 'completed').length;
              return (
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-semibold text-slate-600 uppercase">Tasks ({csCompleted}/{csTasks.length} done)</p>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-1.5 mb-3">
                    <div className="bg-[#1e3a5f] h-1.5 rounded-full transition-all duration-500" style={{ width: `${csTasks.length > 0 ? (csCompleted / csTasks.length) * 100 : 0}%` }} />
                  </div>
                  <div className="space-y-3">
                    {csPhases.map(({ phase, tasks: phaseTasks }) => {
                      const phaseCompleted = phaseTasks.filter(t => t.status === 'completed').length;
                      const phaseColor = PHASE_COLORS[phase] || PHASE_COLORS['General'];
                      const isCollapsed = collapsedPhases[`cs_${phase}`];
                      return (
                        <div key={phase} className={`rounded-lg border-l-4 ${phaseColor}`} data-testid={`cs-phase-${phase.replace(/\s+/g, '-').toLowerCase()}`}>
                          <button onClick={() => setCollapsedPhases(prev => ({ ...prev, [`cs_${phase}`]: !prev[`cs_${phase}`] }))} className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-white/50 transition-colors">
                            <div className="flex items-center gap-2">
                              <CircleDot className="w-3.5 h-3.5 text-slate-500" />
                              <span className="text-xs font-bold text-slate-700 uppercase tracking-wide">{phase}</span>
                              <span className="text-[10px] text-slate-400 font-medium">{phaseCompleted}/{phaseTasks.length}</span>
                            </div>
                            {isCollapsed ? <ChevronDown className="w-3.5 h-3.5 text-slate-400" /> : <ChevronUp className="w-3.5 h-3.5 text-slate-400" />}
                          </button>
                          {!isCollapsed && (
                            <div className="px-4 pb-3 space-y-1.5">
                              {phaseTasks.map(task => (
                                <div key={task.globalIdx} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-3" data-testid={`cs-task-${task.globalIdx}`}>
                                  <p className={`text-sm flex-1 font-medium ${task.status === 'completed' ? 'text-slate-400 line-through' : 'text-slate-700'}`}>{task.name}</p>
                                  <select value={task.status} onChange={e => updateCSTask(task.globalIdx, 'status', e.target.value)} className={`text-xs font-medium px-2 py-1 rounded-full border-0 outline-none cursor-pointer ${TASK_STATUS_COLORS[task.status]}`}>
                                    <option value="not_started">Not Started</option>
                                    <option value="in_progress">In Progress</option>
                                    <option value="completed">Completed</option>
                                  </select>
                                  <input type="date" value={task.due_date || ''} onChange={e => updateCSTask(task.globalIdx, 'due_date', e.target.value)} className="text-xs text-slate-500 border border-slate-200 rounded px-2 py-1" />
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })()}

            {/* CS Documents */}
            <div>
              <p className="text-xs font-semibold text-slate-600 uppercase mb-2">Documents</p>
              <div className="space-y-2">
                {(caseData.cs_scope?.documents || []).map((doc, i) => (
                  <div key={i} className="flex items-center gap-3 bg-white border border-slate-200 rounded-lg p-3" data-testid={`cs-doc-${i}`}>
                    <p className="text-sm text-slate-700 flex-1 font-medium">{doc.name}</p>
                    <select
                      value={doc.status}
                      onChange={e => updateCSDoc(i, 'status', e.target.value)}
                      className={`text-xs font-medium px-2 py-1 rounded-full border-0 outline-none cursor-pointer ${DOC_STATUS_COLORS[doc.status]}`}
                    >
                      <option value="pending">Pending</option>
                      <option value="uploaded">Uploaded</option>
                      <option value="approved">Approved</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* PARTNER ACTIVITY LOG */}
      <Card className="mb-4" data-testid="activity-log">
        <button onClick={() => setShowActivity(!showActivity)} className="w-full flex items-center justify-between p-5 hover:bg-slate-50 transition-colors">
          <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
            <Activity className="w-4 h-4 text-[#1e3a5f]" /> Partner Activity Log
            {(caseData.activity_log || []).length > 0 && <span className="text-[10px] bg-[#1e3a5f]/10 text-[#1e3a5f] px-1.5 py-0.5 rounded-full">{(caseData.activity_log || []).length}</span>}
          </span>
          {showActivity ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </button>
        {showActivity && (
          <div className="px-5 pb-5 border-t border-slate-100 pt-4">
            {(caseData.activity_log || []).length === 0 ? (
              <p className="text-xs text-slate-400">No partner activity yet. Updates will appear here when CA/CS partners modify tasks, documents, or advance the case status.</p>
            ) : (
              <div className="relative pl-5 space-y-0">
                <div className="absolute left-[7px] top-2 bottom-2 w-px bg-slate-200" />
                {(caseData.activity_log || []).slice().reverse().map((entry, i) => (
                  <div key={i} className="relative pb-4 last:pb-0" data-testid={`activity-${i}`}>
                    <div className={`absolute left-[-17px] top-1.5 w-2.5 h-2.5 rounded-full border-2 border-white ${entry.type === 'status_change' ? 'bg-green-500' : entry.type === 'task_update' ? 'bg-amber-500' : 'bg-blue-500'}`} />
                    <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${entry.role === 'CA' ? 'bg-indigo-100 text-indigo-700' : 'bg-teal-100 text-teal-700'}`}>{entry.role}</span>
                        <span className="text-xs font-medium text-slate-700">{entry.by}</span>
                        <span className="text-[10px] text-slate-400 ml-auto">{new Date(entry.at).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                      </div>
                      <p className="text-sm text-slate-600">{entry.detail}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </Card>

      {/* STATUS STREAM — single timeline shared between admin + assigned CA + assigned CS */}
      <Card data-testid="case-status-stream">
        <CardContent className="p-5">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-[#1e3a5f]" />
              <h3 className="text-sm font-semibold text-slate-700">Status Stream</h3>
              <span className="text-[10px] text-slate-400">Shared with assigned CA + CS</span>
            </div>
            <span className="text-[10px] text-slate-400">{(caseData.status_stream || caseData.notes || []).length} entries</span>
          </div>

          {/* Composer */}
          <div className="flex flex-col md:flex-row gap-2 mb-4">
            <select
              value={newKind}
              onChange={e => setNewKind(e.target.value)}
              className="h-9 text-xs px-2 rounded border border-slate-200 bg-white shrink-0"
              data-testid="stream-kind-select"
            >
              <option value="note">Note</option>
              <option value="status">Status update</option>
              <option value="milestone">Milestone</option>
            </select>
            <Input
              value={newNote}
              onChange={e => setNewNote(e.target.value)}
              placeholder="What happened on this case? (visible to assigned partners)"
              className="h-9 text-sm flex-1"
              onKeyDown={e => { if (e.key === 'Enter') addStream(); }}
              data-testid="stream-input"
            />
            <Button size="sm" onClick={addStream} className="bg-[#1e3a5f] hover:bg-[#152d4a] h-9" data-testid="add-stream-btn">
              <Plus className="w-3.5 h-3.5 mr-1" /> Post
            </Button>
          </div>

          {/* Stream List (status_stream preferred; fall back to legacy notes) */}
          <div className="space-y-2">
            {(() => {
              const stream = (caseData.status_stream && caseData.status_stream.length)
                ? [...caseData.status_stream]
                : (caseData.notes || []).map(n => ({ ...n, kind: 'note', by_role: 'admin', by_name: n.by }));
              if (stream.length === 0) return <p className="text-xs text-slate-400">No updates yet</p>;
              return stream.slice().reverse().map((e, i) => {
                const roleStyle = e.by_role === 'CA'
                  ? 'bg-blue-50 border-blue-200'
                  : e.by_role === 'CS'
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-slate-50 border-slate-200';
                const roleChipStyle = e.by_role === 'CA'
                  ? 'bg-blue-100 text-blue-700'
                  : e.by_role === 'CS'
                  ? 'bg-emerald-100 text-emerald-700'
                  : 'bg-slate-200 text-slate-600';
                const kindChip = e.kind === 'milestone'
                  ? <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0">MILESTONE</span>
                  : e.kind === 'status'
                  ? <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 shrink-0">STATUS</span>
                  : null;
                return (
                  <div key={e.id || i} className={`border rounded-lg p-3 ${roleStyle}`} data-testid={`stream-${i}`}>
                    <div className="flex items-start gap-2 mb-1">
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded shrink-0 ${roleChipStyle}`}>{(e.by_role || 'admin').toUpperCase()}</span>
                      {kindChip}
                      <span className="text-[10px] text-slate-500 ml-auto">{new Date(e.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                    </div>
                    <p className="text-sm text-slate-700">{e.text}</p>
                    <p className="text-[10px] text-slate-400 mt-1">{e.by_name || e.by}</p>
                  </div>
                );
              });
            })()}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
